import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useLocationStore } from "@/lib/store";
import { ACCEPT_IMAGES, isImageFile } from "@/lib/image-files";
import { DD } from "@/components/posts/constants";
import type { MediaFile, Location } from "@shared/schema";
import { Upload, Trash2, Image as ImageIcon, Copy, Check, Sparkles, X, AlertTriangle, Star, Building2, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const OWNER_FACE_POPUP_KEY = "media_owner_face_popup_v1";

type BrandSlotKey = "logo" | "feature";

interface BrandSlotConfig {
  key: BrandSlotKey;
  title: string;
  helper: string;
  icon: typeof Building2;
  color: string;
}

const BRAND_SLOTS: BrandSlotConfig[] = [
  {
    key: "logo",
    title: "Business Logo",
    helper: "Used by Product Forge, branded post cards, and reports. PNG with transparent background works best.",
    icon: Building2,
    color: DD.accent,
  },
  {
    key: "feature",
    title: "Feature Image",
    helper: "A hero photo of your business. Used as the fallback when posts and products don't have their own image.",
    icon: Star,
    color: DD.purple || "#a855f7",
  },
];

export default function MediaPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [taggedUploading, setTaggedUploading] = useState<BrandSlotKey | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showOwnerFacePopup, setShowOwnerFacePopup] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(OWNER_FACE_POPUP_KEY)) setShowOwnerFacePopup(true);
    } catch {}
  }, []);

  const dismissOwnerFacePopup = useCallback(() => {
    try { localStorage.setItem(OWNER_FACE_POPUP_KEY, "1"); } catch {}
    setShowOwnerFacePopup(false);
  }, []);

  const { data: files, isLoading } = useQuery<MediaFile[]>({
    queryKey: ["/api/media", selectedLocationId],
    queryFn: () => fetch(`/api/media/${selectedLocationId}`, { credentials: "include" }).then(r => r.json()),
    enabled: !!selectedLocationId,
  });

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });
  const location = locations?.find(l => l.id === selectedLocationId) as
    | (Location & { logoUrl?: string | null; featureImageUrl?: string | null })
    | undefined;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/media/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media", selectedLocationId] });
      toast({ title: "Deleted", description: "File removed from library" });
    },
  });

  const MAX_UPLOAD = 20;

  const uploadFiles = useCallback(async (allImages: File[], tag?: BrandSlotKey) => {
    if (!selectedLocationId) return;
    const formData = new FormData();
    formData.append("locationId", selectedLocationId);
    if (tag) formData.append("tag", tag);
    allImages.forEach(f => formData.append("images", f));
    const res = await fetch("/api/media", { method: "POST", body: formData, credentials: "include" });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || "Upload failed");
    }
    queryClient.invalidateQueries({ queryKey: ["/api/media", selectedLocationId] });
    queryClient.invalidateQueries({ queryKey: ["/api/locations", selectedLocationId, "rtd-progress"] });
    if (tag) queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
  }, [selectedLocationId]);

  const handleUpload = useCallback(async (fileList: FileList | File[]) => {
    if (!selectedLocationId) return;
    const allImages = Array.from(fileList).filter(isImageFile);
    if (!allImages.length) {
      toast({ title: "Invalid files", description: "Only image files are supported", variant: "destructive" });
      return;
    }
    if (allImages.length > MAX_UPLOAD) {
      toast({
        title: "Too many files",
        description: `You can upload up to ${MAX_UPLOAD} images at a time. You selected ${allImages.length}.`,
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    try {
      await uploadFiles(allImages);
      toast({ title: "Uploaded", description: `${allImages.length} image${allImages.length > 1 ? "s" : ""} added to library` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message || "Something went wrong", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }, [selectedLocationId, toast, uploadFiles]);

  const handleTaggedUpload = useCallback(async (file: File, tag: BrandSlotKey) => {
    if (!selectedLocationId) return;
    if (!isImageFile(file)) {
      toast({ title: "Invalid file", description: "Only image files are supported", variant: "destructive" });
      return;
    }
    setTaggedUploading(tag);
    try {
      await uploadFiles([file], tag);
      toast({
        title: tag === "logo" ? "Logo updated" : "Feature image updated",
        description: tag === "logo"
          ? "Product Forge and branded cards will use this logo."
          : "Will be used as the default hero/fallback image.",
      });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message || "Something went wrong", variant: "destructive" });
    } finally {
      setTaggedUploading(null);
    }
  }, [selectedLocationId, toast, uploadFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const copyUrl = (file: MediaFile) => {
    navigator.clipboard.writeText(file.url);
    setCopiedId(file.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteSelected = () => {
    selectedIds.forEach(id => deleteMutation.mutate(id));
    setSelectedIds(new Set());
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!selectedLocationId) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: DD.bg, color: DD.textSecondary }}>
        <p>Select a location to manage media</p>
      </div>
    );
  }

  const currentLogoUrl = location?.logoUrl || null;
  const currentFeatureUrl = location?.featureImageUrl || null;
  const slotValues: Record<BrandSlotKey, string | null> = {
    logo: currentLogoUrl,
    feature: currentFeatureUrl,
  };

  const hasTag = (f: MediaFile, t: string) =>
    Array.isArray((f as any).tags) && (f as any).tags.includes(t);
  // Mirror the server's RTD "images" completion logic exactly: logo/feature are
  // satisfied by either the location field OR a tagged media file.
  const hasLogo = !!currentLogoUrl || !!files?.some(f => hasTag(f, "logo"));
  const hasFeature = !!currentFeatureUrl || !!files?.some(f => hasTag(f, "feature"));
  const hasNormalImage = !!files?.some(f => !hasTag(f, "logo") && !hasTag(f, "feature"));
  const imageReqs = [
    { key: "logo", label: "Business logo", done: hasLogo },
    { key: "feature", label: "Feature image", done: hasFeature },
    { key: "normal", label: "At least one photo", done: hasNormalImage },
  ];
  const imageReqsComplete = imageReqs.every(r => r.done);

  return (
    <div className="flex-1 overflow-auto" style={{ background: DD.bg }}>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: DD.text }} data-testid="heading-media">Media Library</h1>
            <p className="text-sm mt-1" style={{ color: DD.textSecondary }}>
              Upload images for your GBP posts, products, and profile
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RtdOptOut
              locationId={selectedLocationId}
              taskIds={["images"]}
              stageName="Media"
              warningTitle="Skip uploading photos for now?"
              warningBody={
                <p>
                  Heads up — GBPs with fresh, original photos get significantly more views and clicks.
                  Google's algorithm rewards listings with regular photo uploads, and customers are far
                  more likely to choose a business when they can see your work, your team, and your
                  location. Skipping photos usually means lower engagement and slower ranking growth.
                </p>
              }
            />
            {selectedIds.size > 0 && (
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                style={{ background: "rgba(239,68,68,0.15)", color: DD.danger, border: `1px solid rgba(239,68,68,0.3)` }}
                onClick={deleteSelected}
                data-testid="button-delete-selected"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete {selectedIds.size}
              </button>
            )}
            <button
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ background: DD.accent }}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="button-upload-media"
            >
              <Upload className="w-4 h-4" /> Upload Images
            </button>
          </div>
        </div>
        <div className="mb-4">
          <RtdOptOutBanner
            locationId={selectedLocationId}
            taskIds={["images"]}
            stageName="Media"
          />
        </div>

        {!imageReqsComplete && (
          <div
            className="mb-4 rounded-lg flex items-start gap-3 px-4 py-3"
            style={{
              background: "rgba(234,179,8,0.08)",
              border: "1px solid rgba(234,179,8,0.3)",
            }}
            data-testid="banner-brand-assets-missing"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#eab308" }} />
            <div className="flex-1 text-sm">
              <p className="font-medium" style={{ color: "#facc15" }}>
                Finish your Media step to continue the Road to Domination
              </p>
              <p className="text-xs mt-0.5" style={{ color: DD.textSecondary }}>
                Upload all three so AI can create personalized, on-brand visuals. You'll keep
                returning to this step until each one is in place.
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                {imageReqs.map(req => (
                  <div
                    key={req.key}
                    className="flex items-center gap-1.5 text-xs"
                    style={{ color: req.done ? "#4ade80" : DD.textSecondary }}
                    data-testid={`req-image-${req.key}`}
                  >
                    {req.done ? (
                      <Check className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border shrink-0" style={{ borderColor: "#eab308" }} />
                    )}
                    {req.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {BRAND_SLOTS.map(slot => (
            <BrandSlotZone
              key={slot.key}
              slot={slot}
              currentUrl={slotValues[slot.key]}
              uploading={taggedUploading === slot.key}
              onUpload={(file) => handleTaggedUpload(file, slot.key)}
            />
          ))}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_IMAGES}
          multiple
          className="hidden"
          onChange={e => e.target.files && handleUpload(e.target.files)}
          data-testid="input-media-file"
        />

        <div
          className={`rounded-xl mb-6 flex flex-col items-center justify-center py-12 transition-all cursor-pointer ${isDragOver ? "scale-[1.005]" : ""}`}
          style={{
            border: `2px dashed ${isDragOver ? DD.accent : DD.accentBorder}`,
            background: isDragOver ? "rgba(249,115,22,0.06)" : "rgba(249,115,22,0.02)",
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          data-testid="dropzone-media"
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="relative w-16 h-16 mb-4">
                <div
                  className="absolute inset-0 rounded-full animate-spin"
                  style={{ border: `3px solid ${DD.border}`, borderTopColor: DD.accent }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 animate-pulse" style={{ color: DD.accent }} />
                </div>
              </div>
              <p className="text-sm font-semibold" style={{ color: DD.accent }}>Uploading…</p>
              <div className="mt-3 w-48 h-1.5 rounded-full overflow-hidden" style={{ background: DD.border }}>
                <div
                  className="h-full rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${DD.accent}, ${DD.purple})`,
                    animation: "shimmer-bar 2s ease-in-out infinite",
                  }}
                />
              </div>
              <style>{`@keyframes shimmer-bar { 0% { width: 5%; } 50% { width: 75%; } 100% { width: 95%; } }`}</style>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mb-3" style={{ color: DD.accent }} />
              <p className="text-sm font-semibold mb-1" style={{ color: DD.accent }}>
                Drag & Drop Images Here
              </p>
              <p className="text-xs" style={{ color: DD.textTertiary }}>
                or click to browse — supports JPG, PNG, WebP (max {MAX_UPLOAD} per upload)
              </p>
            </>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg animate-pulse" style={{ background: DD.surface }} />
            ))}
          </div>
        ) : !files?.length ? (
          <div className="flex flex-col items-center justify-center py-20">
            <ImageIcon className="w-16 h-16 mb-4" style={{ color: DD.textTertiary }} />
            <p className="text-sm font-medium" style={{ color: DD.textSecondary }}>No media yet</p>
            <p className="text-xs mt-1" style={{ color: DD.textTertiary }}>Upload images to build your media library</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="grid-media">
            {files.map(file => {
              const tags = (Array.isArray((file as any).tags) ? (file as any).tags : []) as string[];
              const tagLabel = tags.includes("logo") ? "LOGO" : tags.includes("feature") ? "FEATURE" : null;
              return (
                <div
                  key={file.id}
                  className="group relative rounded-lg overflow-hidden cursor-pointer transition-all"
                  style={{
                    border: selectedIds.has(file.id) ? `2px solid ${DD.accent}` : `1px solid ${DD.border}`,
                    background: DD.surfaceSolid,
                  }}
                  onClick={() => toggleSelect(file.id)}
                  data-testid={`media-item-${file.id}`}
                >
                  <div className="aspect-square">
                    <img
                      src={file.url}
                      alt={file.originalName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  {tagLabel && (
                    <div
                      className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide"
                      style={{
                        background: tagLabel === "LOGO" ? DD.accent : (DD.purple || "#a855f7"),
                        color: "#fff",
                      }}
                      data-testid={`badge-tag-${tagLabel.toLowerCase()}-${file.id}`}
                    >
                      {tagLabel}
                    </div>
                  )}
                  <div
                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2"
                  >
                    <p className="text-[10px] text-white truncate">{file.originalName}</p>
                    <p className="text-[9px] text-white/50">{formatSize(file.sizeBytes)}</p>
                    <div className="flex gap-1 mt-1.5">
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
                        onClick={e => { e.stopPropagation(); copyUrl(file); }}
                        data-testid={`button-copy-url-${file.id}`}
                      >
                        {copiedId === file.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copiedId === file.id ? "Copied" : "URL"}
                      </button>
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                        onClick={e => { e.stopPropagation(); deleteMutation.mutate(file.id); }}
                        data-testid={`button-delete-${file.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {selectedIds.has(file.id) && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: DD.accent }}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {files && files.length > 0 && (
          <p className="text-xs mt-4 text-center" style={{ color: DD.textTertiary }} data-testid="text-media-count">
            {files.length} image{files.length !== 1 ? "s" : ""} in library
          </p>
        )}
      </div>

      <Dialog open={showOwnerFacePopup} onOpenChange={(open) => { if (!open) dismissOwnerFacePopup(); }}>
        <DialogContent
          className="max-w-lg"
          style={{ background: DD.surfaceSolid, border: `1px solid ${DD.border}`, color: DD.text }}
          data-testid="dialog-owner-face"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white text-xl">
              <Users className="w-6 h-6" style={{ color: DD.accent }} />
              Add photos of your owner & staff
            </DialogTitle>
          </DialogHeader>
          <p className="text-base leading-relaxed" style={{ color: DD.textSecondary }}>
            Adding pictures with the face of the owner or staff will ensure the AI images we
            generate have a more local, authentic feel.{" "}
            <span className="font-semibold" style={{ color: DD.text }}>People do business with people.</span>
          </p>
          <DialogFooter>
            <Button
              className="text-white font-semibold w-full"
              style={{ background: DD.accent }}
              onClick={dismissOwnerFacePopup}
              data-testid="button-understand-owner-face"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BrandSlotZone({
  slot,
  currentUrl,
  uploading,
  onUpload,
}: {
  slot: BrandSlotConfig;
  currentUrl: string | null;
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const Icon = slot.icon;
  const hasImage = !!currentUrl;

  const handleFiles = (fileList: FileList | File[] | null) => {
    if (!fileList) return;
    const file = Array.from(fileList)[0];
    if (file) onUpload(file);
  };

  return (
    <div
      className={`relative rounded-xl p-4 transition-all cursor-pointer ${isDragOver ? "scale-[1.01]" : ""}`}
      style={{
        border: hasImage
          ? `1px solid ${DD.border}`
          : `2px dashed ${isDragOver ? slot.color : `${slot.color}66`}`,
        background: hasImage ? DD.surfaceSolid : `${slot.color}0d`,
      }}
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setIsDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      data-testid={`zone-brand-${slot.key}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_IMAGES}
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
        data-testid={`input-brand-${slot.key}`}
      />
      <div className="flex items-start gap-3">
        <div
          className="w-20 h-20 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
          style={{
            background: hasImage ? "#fff" : `${slot.color}1a`,
            border: hasImage ? `1px solid ${DD.border}` : "none",
          }}
        >
          {hasImage ? (
            <img
              src={currentUrl!}
              alt={slot.title}
              className="w-full h-full object-contain"
              data-testid={`img-brand-${slot.key}`}
            />
          ) : (
            <Icon className="w-8 h-8" style={{ color: slot.color }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold" style={{ color: DD.text }} data-testid={`text-slot-title-${slot.key}`}>
              {slot.title}
            </h3>
            {hasImage ? (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80" }}
                data-testid={`status-slot-${slot.key}`}
              >
                SET
              </span>
            ) : (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(234,179,8,0.15)", color: "#facc15" }}
                data-testid={`status-slot-${slot.key}`}
              >
                MISSING
              </span>
            )}
          </div>
          <p className="text-xs leading-relaxed" style={{ color: DD.textSecondary }}>
            {slot.helper}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs font-medium" style={{ color: slot.color }}>
            {uploading ? (
              <>
                <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="w-3.5 h-3.5" />
                {hasImage ? "Replace image" : "Click or drop image to upload"}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
